import React from 'react';
import AdminShell from './AdminShell';
import { CheckCircle2, Circle, ExternalLink } from 'lucide-react';
import { analyticsIds } from '../../lib/analytics';

const EVENTS = [
  ['page_view / virtual_page_view', 'Full page load on every main-nav click (built-in GTM Page View). virtual_page_view fires for instant blog navigation. Params: page_type, page_path, page_title', 'Paths, exits, drop-offs, time on page'],
  ['demo_cta_click', 'Any "Request demo" button. Params: cta_location (header / body / footer)', 'Funnel step'],
  ['demo_request_submit', 'Demo form submitted successfully. Params: product', 'CONVERSION'],
  ['contact_submit', 'Contact page form submitted. Params: product', 'CONVERSION'],
  ['pricing_plan_click', 'Plan CTA on /pricing. Params: label = plan name', 'Funnel step'],
  ['nav_click', 'Header navigation links. Params: label, href', 'Behaviour'],
  ['blog_related_click', '"Related posts" card. Params: label = slug', 'Content engagement'],
  ['scroll_depth', '25 / 50 / 75 / 100 % on blog posts. Params: percent, label = path', 'Engagement'],
  ['read_complete', 'Reader reached the end of a blog post', 'Engagement'],
  ['outbound_click', 'Any link leaving the site. Params: label = URL', 'Behaviour'],
];

const FUNNELS = [
  { name: 'Demo request', steps: ['page_view (page_type = home or product)', 'page_view (page_type = pricing)', 'demo_cta_click', 'demo_request_submit'] },
  { name: 'Blog to lead', steps: ['page_view (page_type = blog_post)', 'scroll_depth (percent = 50)', 'page_view (page_type = contact) or demo_cta_click', 'contact_submit or demo_request_submit'] },
  { name: 'Pricing to contact', steps: ['page_view (page_type = pricing)', 'pricing_plan_click', 'contact_submit'] },
];

const Status = ({ ok, label, value }) => (
  <div className="flex items-center gap-3 text-sm">
    {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Circle className="w-4 h-4 text-ink-3" />}
    <span className="text-ink">{label}</span>
    <span className="text-ink-3 text-xs">{ok ? value : 'not configured'}</span>
  </div>
);

const Card = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-[var(--line-2)] p-6">
    <h3 className="text-base font-semibold text-ink mb-3">{title}</h3>
    {children}
  </div>
);

const AdminAnalytics = () => {
  const ids = analyticsIds();
  return (
    <AdminShell title="Analytics setup">
      <div className="space-y-6" data-testid="analytics-setup">
        <Card title="Status">
          <div className="space-y-2">
            <Status ok={!!ids.gtm} label="Google Tag Manager container" value={ids.gtm} />
            <Status ok={!!ids.ga4} label="Google Analytics 4 (direct)" value={ids.ga4} />
            <Status ok={!!ids.clarity} label="Microsoft Clarity (heatmaps & recordings)" value={ids.clarity} />
            <Status ok={!!process.env.REACT_APP_GSC_VERIFICATION} label="Google Search Console verification" value="tag present" />
          </div>
          <p className="text-xs text-ink-3 mt-4">
            Add <code>REACT_APP_GTM_ID</code>, <code>REACT_APP_GA4_ID</code>, <code>REACT_APP_CLARITY_ID</code> and <code>REACT_APP_GSC_VERIFICATION</code> to <code>frontend/.env</code> and redeploy. Tracking loads only after a visitor accepts the cookie bar (Consent Mode v2) and never on admin pages.
          </p>
          {ids.gtm && (
            <div className="mt-4 rounded-xl border border-[var(--badge-border)] bg-[var(--badge-bg)] p-4 text-xs text-ink-2 space-y-1.5" data-testid="gtm-guidance">
              <div className="font-semibold text-ink">GTM is active — notes for the marketing team</div>
              <div>• Configure GA4 <em>inside</em> GTM (Google tag) and leave <code>REACT_APP_GA4_ID</code> empty, or the other way round — never both, or every hit is counted twice.</div>
              <div>• Main navigation and CTAs load each page as a full document, so GTM's built-in <strong>Page View</strong> trigger fires per page. Only inside the blog (list → post → related post) navigation is instant; those fire a <code>virtual_page_view</code> Custom Event — add it as a second trigger on the GA4 tag (variables: page_path, page_title, page_type).</div>
              <div>• Every event below is also pushed to <code>dataLayer</code> as <code>{'{ event: "<name>", ...params }'}</code> — add Custom Event triggers with the same names.</div>
              <div>• Consent: the site sets Consent Mode v2 defaults (denied) and updates on Accept. In GTM → Admin → Container settings, enable consent overview and require <code>analytics_storage</code> on tags.</div>
            </div>
          )}
        </Card>

        <Card title="Where to create the accounts">
          <ul className="text-sm text-ink-2 space-y-1.5">
            <li>• GA4: <a className="underline inline-flex items-center gap-1" href="https://analytics.google.com" target="_blank" rel="noreferrer">analytics.google.com <ExternalLink className="w-3 h-3" /></a> → Admin → Create property → Web data stream → copy the Measurement ID (G-…). Turn on Enhanced measurement.</li>
            <li>• Clarity: <a className="underline inline-flex items-center gap-1" href="https://clarity.microsoft.com" target="_blank" rel="noreferrer">clarity.microsoft.com <ExternalLink className="w-3 h-3" /></a> → New project → copy the Project ID. In Settings → Setup, connect to GA4 to see recordings next to GA sessions.</li>
            <li>• Search Console: <a className="underline inline-flex items-center gap-1" href="https://search.google.com/search-console" target="_blank" rel="noreferrer">search.google.com/search-console <ExternalLink className="w-3 h-3" /></a> → URL-prefix property → HTML tag → paste the <code>content</code> value. Then submit <code>/api/sitemap.xml</code> and link GA4 ↔ Search Console.</li>
          </ul>
        </Card>

        <Card title="Events the site sends">
          <table className="w-full text-sm">
            <thead className="text-ink-3 text-[11px] uppercase tracking-widest">
              <tr><th className="text-left py-2">Event</th><th className="text-left py-2">When / params</th><th className="text-left py-2">Use</th></tr>
            </thead>
            <tbody>
              {EVENTS.map(([e, when, use]) => (
                <tr key={e} className="border-t border-[var(--line-2)]">
                  <td className="py-2 font-mono text-xs text-ink whitespace-nowrap">{e}</td>
                  <td className="py-2 text-ink-2">{when}</td>
                  <td className={`py-2 text-xs ${use === 'CONVERSION' ? 'text-emerald-700 font-semibold' : 'text-ink-3'}`}>{use}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-ink-3 mt-3">In GA4 → Admin → Events, mark <code>demo_request_submit</code> and <code>contact_submit</code> as key events (conversions). Register <code>page_type</code>, <code>cta_location</code>, <code>percent</code> and <code>label</code> as custom dimensions (Admin → Custom definitions) so they appear in reports.</p>
        </Card>

        <Card title="Funnels to build (GA4 → Explore → Funnel exploration)">
          <div className="grid md:grid-cols-3 gap-4">
            {FUNNELS.map((f) => (
              <div key={f.name} className="rounded-xl border border-[var(--line-2)] p-4">
                <div className="text-sm font-medium text-ink mb-2">{f.name}</div>
                <ol className="text-xs text-ink-2 space-y-1 list-decimal pl-4">
                  {f.steps.map((s) => <li key={s}>{s}</li>)}
                </ol>
              </div>
            ))}
          </div>
          <p className="text-xs text-ink-3 mt-3">Use "Path exploration" with an ending point of <code>page_view</code> for exit-page analysis, and "Path exploration" starting from <code>session_start</code> for user journeys. Engagement → Pages shows average engagement time per page.</p>
        </Card>

        <Card title="Lead attribution">
          <p className="text-sm text-ink-2">Every contact and demo submission stores first-touch UTM source / medium / campaign, the referrer, the landing page and the page it was submitted from. See the Channel column in Contacts. Tag campaign links like <code>?utm_source=linkedin&amp;utm_medium=social&amp;utm_campaign=roi-playbook</code>.</p>
        </Card>
      </div>
    </AdminShell>
  );
};

export default AdminAnalytics;
